import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { estimateFeesPerGas, estimateGas, getAccount, getBalance, prepareTransactionRequest, readContracts, reconnect, sendTransaction, signMessage, waitForTransactionReceipt, writeContract } from '@wagmi/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, firstValueFrom, forkJoin, map, Observable, shareReplay, throwError } from 'rxjs';
import { loanRepayType } from 'src/app/module/youritems/nfts-listing/nft-listing';
import { environment } from 'src/environments/environment';
import { encodeFunctionData, erc20Abi, formatUnits, getAddress, parseEther } from 'viem';
import { walletFeeOverrides } from '../utils/wallet-fee.util';
import { HttpCacheStore } from '../../core/http/http-cache.store';
import { ContractParams, GasEstimationResult } from '../../core/interfaces/transactions/gas.interface';
import { INTERNAL_JSON_RPC_ERROR } from '../constants/errors.const';
import { MethodTypes, NftMediaItem } from '../utils/common.interface';
import { AccountService } from './account.service';
import { TransactionService } from './transaction.service';
import { WebStorageService } from './web-storage.service';
import { WalletStateService } from './wallet-state.service';
import { AppkitService } from './appkit.service';
import { UserStateService } from './user-state.service';
import { ProcessingService } from 'src/app/core/services/processing.service';
import { CurrencyAmountPipe } from '../pipes/currency-amount.pipe';
import { isDigitalAssetCategory } from '../Helper/common-utils';
import { getAssetAppraisal } from '../Helper/collateral-appraisal.utils';

// Enable strict format parsing (MM-DD-YYYY etc.) here so it never depends on another module
// having extended the plugin first — toCustodyEpochMs relies on it for custody-fee timestamps.
dayjs.extend(customParseFormat);

const imageFileExtension = ['png', 'jpeg', 'jpg', 'webp', 'gif', 'tiff', 'ico', 'heif', 'heic'];
const documentFileExtension = ['doc', 'docx', 'odt', 'rtf', 'txt', 'csv', 'pdf'];
const mediaFileExtension = ['webm', 'avif', 'mp4', 'mov', 'ogg', 'wmv'];
const fileSizeLimits = {
    IMAGE: 15, // 10MB
    VIDEO: 500, // 500MB
    FILE: 20, // 20MB
    AUDIO: 100, // 100MB //We are not allowed this
};


@Injectable({
    providedIn: 'root'
})
export class CommonService {

    /**
     * Shared cache for reference-data API calls so the same endpoint isn't
     * re-fetched by every component/route that needs it. `currencies$` is a
     * session-long shared observable; live prices use a short TTL so they stay
     * fresh while still collapsing the burst of duplicate calls on page load.
     */
    private currencies$?: Observable<any>;
    private readonly priceCacheTtlMs = 60_000;
    private readonly ttlPromiseCache = new Map<string, { ts: number; value: Promise<any> }>();

    /**
     * Returns a cached in-flight/resolved promise for `key` when it is younger
     * than `ttlMs`, otherwise runs `factory` and caches the result. A rejected
     * promise is evicted so the next caller retries instead of caching the error.
     */
    private getCachedPromise(key: string, factory: () => Promise<any>, ttlMs: number = this.priceCacheTtlMs): Promise<any> {
        const now = Date.now();
        const hit = this.ttlPromiseCache.get(key);
        if (hit && now - hit.ts < ttlMs) {
            return hit.value;
        }
        const value = factory();
        this.ttlPromiseCache.set(key, { ts: now, value });
        value.catch(() => {
            const current = this.ttlPromiseCache.get(key);
            if (current && current.value === value) {
                this.ttlPromiseCache.delete(key);
            }
        });
        return value;
    }

    /**
     * Drop all cached reference-data responses. Called on logout so a different
     * user (SPA login, no page reload) never sees the previous session's data.
     */
    public clearReferenceDataCache(): void {
        this.currencies$ = undefined;
        this.ttlPromiseCache.clear();
        // CacheInterceptor entries are user/custody/chain-scoped; drop them on the same
        // logout hook so an SPA re-login never reads the previous session's responses.
        this.httpCacheStore.clear();
    }

    private showGridView = new BehaviorSubject(true);
    public showGridViewObservable = this.showGridView.asObservable();

    private signinPageHeader = new BehaviorSubject(false);
    public siginpageObservable = this.signinPageHeader.asObservable();

    private showoverlay = new BehaviorSubject(false);
    showmodaloverlayObservable = this.showoverlay.asObservable();

    private closeModals = new BehaviorSubject(false);
    closeModalsObservable = this.closeModals.asObservable();

    private tabEmitter: any;
    tabEmitterObserve!: Observable<{ type: string; action?: string }>;

    private usdcModalTrigger = new BehaviorSubject<{ tab: 'add' | 'withdraw' } | null>(null);
    usdcModalTrigger$ = this.usdcModalTrigger.asObservable();

    private transactionProvider = new BehaviorSubject({});
    public transactionListener: Observable<any> = this.transactionProvider.asObservable();

    private custodialType = new BehaviorSubject('');
    public custodialTypeObservable = this.custodialType.asObservable();
    public docUrls: any;

    constructor(
        private http: HttpClient,
        private toastr: ToastrService,
        private webStorageService: WebStorageService,
        private walletStateService: WalletStateService,
        private transactionService: TransactionService,
        private router: Router,
        private accountService: AccountService,
        private appkitService: AppkitService,
        private processingService: ProcessingService,
        private userState: UserStateService,
        private currencyAmountPipe: CurrencyAmountPipe,
        private httpCacheStore: HttpCacheStore
    ) {
        this.tabEmitter = new BehaviorSubject({});
        this.tabEmitterObserve = this.tabEmitter.asObservable();
    }

    getNetworkErrorMessage(error: unknown, fallbackMessage: string = 'Something went wrong, try again later.'): string {
        if (this.isNetworkUnavailableError(error)) {
            return 'No internet connection';
        }

        const errorObject = error as {
            error?: {
                data?: {
                    message?: string;
                };
                message?: string;
            };
            shortMessage?: string;
            message?: string;
        };

        return errorObject?.error?.data?.message
            || errorObject?.error?.message
            || errorObject?.shortMessage
            || fallbackMessage;
    }

    private isNetworkUnavailableError(error: unknown): boolean {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return true;
        }

        const errorObject = error as {
            status?: number;
            error?: unknown;
        };

        return errorObject?.status === 0 || errorObject?.error instanceof ProgressEvent;
    }

    setTabEmitter(data: { type: string; action?: string }) {
        this.tabEmitter.next(data);
    }

    openUsdcModal(tab: 'add' | 'withdraw' = 'add') {
        this.usdcModalTrigger.next({ tab });
    }
    /**
     * overlay */
    public setOverlay(type: boolean) {
        this.showoverlay.next(type)
    }
    /**
     * set grid view / table view
     */
    public setView(type: boolean) {
        this.showGridView.next(type);
    }

    /**
     * set hider and header and footer
    */
    public setHeaderHide(type: boolean) {
        this.signinPageHeader?.next(type);
    }

    /**
     * Datas emit
     * @param data
     */
    public transactionEmitter(type: object) {
        this.transactionProvider.next(type);
    }


    /**
     * Emits the selected custodial type
     * @param type The selected custodial type
     */
    setCustodialType(type: string) {
        this.custodialType.next(type);
    }

    /**
     * Uploads image
     * @param network
     * @returns
     */
    public uploadImage(body: any) {
        return firstValueFrom(this.http.post(`${environment.FILE_UPLOAD}/file-upload/document`, body));
    }

    /**
     * Image extension type check
     * @param name 
     * @param [isDocUpload] if we upload docments related means need to pass as true or other wise we don't pass
     * @returns mime type validation 
     */
    public imageMimeTypeValidation(name: string, isDocUpload: boolean = false): Boolean {
        console.log(isDocUpload, "isDocUpload")
        let supportedFileExtension = isDocUpload ? [...documentFileExtension, ...imageFileExtension] : [...imageFileExtension, ...mediaFileExtension];
        var ext = name.substring(name.lastIndexOf('.') + 1);
        if (supportedFileExtension.includes(ext.toLowerCase())) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Handles file size validation
     * @param fileName 
     * @param fileType 
     * @param currentSize 
     * @param [uploadSize] 
     * @returns  
     */
    public handleFileSizeValidation(fileName: any, uploadSize: number = 0, isDocUpload: boolean = false) {
        var extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()
        let fileSize = 0
        let fileType: string = ''
        this.getFileSize(extension, (result: any) => {
            fileSize = result.fileSize
            fileType = result.fileType
        })
        const isValidFile = this.imageMimeTypeValidation(fileName, isDocUpload)
        const Min_File_Size = 10
        const fileInKb = uploadSize / 1024
        const fileInMb = fileSize * 1024 * 1024
        if (!isValidFile) return 'File not supported.'
        if (fileInKb <= Min_File_Size) return `"${fileName}" is smaller than ${Min_File_Size}KB.`
        if (uploadSize >= fileInMb) return `${fileType} "${fileName}" exceeds ${fileSize}MB.`

        return true

    }

    /**
     * Gets file size
     * @param fileType 
     */
    public getFileSize(fileType: any, callBack: (result: { fileSize: number, fileType: string }) => any) {
        let fileSize: any;
        let getFileType: string = ''
        switch (true) {
            case imageFileExtension.includes(fileType):
                fileSize = fileSizeLimits['IMAGE']
                getFileType = 'Image'
                break;
            case mediaFileExtension.includes(fileType):
                fileSize = fileSizeLimits['VIDEO']
                getFileType = 'Video'
                break;

            case documentFileExtension.includes(fileType):
                fileSize = fileSizeLimits['FILE']
                getFileType = 'File'
                break;
            default:
                fileSize = fileSizeLimits['FILE']
                getFileType = 'File'
                break;
        }

        callBack({
            fileSize,
            fileType: getFileType
        })
    }


    /**
     * Images provenance documents
     * @param name 
     * @returns true if provenance documents 
     */
    imageProvenanceDocuments(name: string): boolean {
        const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
        return documentFileExtension.includes(ext);
    }

    public getCurrencies() {
        // Currencies are a static reference list — fetch once per session and
        // share the single response with every component/route that needs it.
        if (!this.currencies$) {
            this.currencies$ = this.http.get(`${environment.API_BASE_URL_V3}/currencies`).pipe(
                shareReplay({ bufferSize: 1, refCount: false }),
                catchError((err) => {
                    // Drop the cache so a failed load is retried on the next call.
                    this.currencies$ = undefined;
                    return throwError(() => err);
                })
            );
        }
        return this.currencies$;
    }

    getNonce() {
        return firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/nonce`))
    }

    // Batch B (CertiK REA-24 nonce+deadline): mint a per-signer signature-replay nonce + deadline.
    // NOTE: distinct from getNonce() above, which returns the loanId. Optional expiredDays lets the
    // backend derive the deadline (unix seconds, server clock) from the offer/counter validity window.
    getSignatureNonce(expiredDays?: number | string | null) {
        const days = expiredDays != null ? String(expiredDays).trim() : '';
        const query = days !== '' ? `?expired_days=${encodeURIComponent(days)}` : '';
        return firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/signature-nonce${query}`))
    }

    // Batch B: friendly text for the two new signature-replay reverts. viem exposes the custom-error
    // name on the BaseError cause chain (only decodable because SignatureExpired/NonceAlreadyUsed are
    // now in nft-loan.json). Returns null when it's not one of these — callers fall through as before.
    getSignatureRevertMessage(error: any): string | null {
        const name = error?.cause?.data?.errorName
            || error?.cause?.cause?.data?.errorName
            || error?.data?.errorName
            || '';
        const blob = `${name} ${error?.shortMessage || ''} ${error?.message || ''}`;
        if (/SignatureExpired/i.test(blob)) return 'This offer has expired. Please ask the other party to submit it again.';
        if (/NonceAlreadyUsed/i.test(blob)) return 'This offer was already accepted or is no longer valid. Please refresh and try again.';
        return null;
    }

    // Overwrites error.shortMessage in place so the existing per-component handleError toast surfaces
    // the friendly text. No-op for unrelated errors.
    applySignatureRevertMessage(error: any): void {
        const msg = this.getSignatureRevertMessage(error);
        if (msg && error) error.shortMessage = msg;
    }

    async sendTransactionOld(account: any, contractAddress: string, abi: any, requiredGas: any, args: any, txId?: string, isExchange: boolean = false) {
        try {
            let regulated = this.walletStateService.getRegulated();
            // let feeData = await estimateFeesPerGas(this.appkitService.adapter.wagmiConfig, {
            //     chainId: account.networkId,
            //     type: 'legacy'
            // });
            let feeData = await estimateFeesPerGas(this.appkitService.adapter.wagmiConfig);
            // console.log('required gas', requiredGas);

            // console.log('feeData-send', feeData);

            const params: any = {
                account: account.walletAddress,
                to: contractAddress,
                data: abi,
                chainId: account.networkId,
                // gasPrice: Number(feeData.gasPrice) ? Number(feeData.gasPrice) * 2 : undefined,
                // gas: Number(requiredGas) * 2,
                value: args.value ? regulated ? Number(parseEther(args.value.toString())) : parseEther(args.value.toString()) : 0
            };
            if (!isExchange) {
                params.gas = Number(requiredGas) * 2;
                params.gasPrice = Number(feeData.gasPrice) ? Number(feeData.gasPrice) * 2 : undefined;
            }
            if (regulated) return this.sendTransactionRegulated(params, args, txId);
            else return this.sendTransactionUnregulated(params, account, txId);
        } catch (error) {
            throw error
        }
    }

    
    /**
     * Sends a transaction to the blockchain
     * @param account The user account data
     * @param contractAddress The contract address
     * @param args The contract arguments
     * @param txId The transaction ID to store the transaction data against (optional)
     * @param isExchange Whether this transaction is an exchange (optional, default false)
     * @returns A promise that resolves with the transaction hash
     * @throws If the transaction fails for any reason
     */
    async sendTransaction(
        account: any,
        contractAddress: string,
        args: any,
        txId?: string,
        isExchange: boolean = false
      ) {
        // Start processing (prevents update popup)
        this.processingService.start();

        try {
          const regulatedFlag = this.walletStateService.getRegulated();
          const user = JSON.parse(this.webStorageService.getLocalStorage('user') || '{}');
          // Only use the Fireblocks regulated path when the user actually has a
          // Fireblocks vault address. Without it the backend rejects the request
          // with "accountId is required". Self-custody users (or regulated users
          // whose Fireblocks account hasn't been provisioned yet) fall back to
          // the Wagmi unregulated path.
          const regulated = regulatedFlag && !!user.fire_block_address;

          // Load ABI dynamically using switch-case
          const approveAbi = await this.loadAbi(args.abiType);

          const params: any = {
            account: account.walletAddress,
            to: contractAddress,
            abi: approveAbi,
            chainId: account.networkId,
            value: args.value
              ? regulated
                ? Number(parseEther(args.value.toString()))
                : parseEther(args.value.toString())
              : 0
          };

          // Regulated vs Unregulated flow
          let result;
          if (regulated) {
            result = await this.sendTransactionRegulated(params, args, txId);
          } else {
            const wagmiResult = await this.sendTransactionUnregulated(params, args, txId);
            // When falling back from a regulated flag to the unregulated path
            // (because fire_block_address is absent), wrap the wagmi receipt in the
            // Fireblocks response shape so callers that do
            // `if (this.regulated) tx = JSON.parse(tx.data)` still work.
            result = regulatedFlag ? { data: JSON.stringify(wagmiResult) } : wagmiResult;
          }

          return result;

        } catch (error) {
          throw error;

        } finally {
          // Stop processing ALWAYS (success or error)
          this.processingService.stop();
        }
      }
      
      



/**
 * Dynamically loads an ABI based on the type parameter.
 * @param type - The type of ABI to load. Supported types are 'erc721', 'loan', 'erc20', 'lazyMint', and 'exchange'.
 * @returns A promise that resolves with the loaded ABI.
 * @throws Error - If the ABI type is not supported.
 */
      private async loadAbi(type: string): Promise<any> {
        switch (type) {
          case 'erc721': {
            const module = await import('../../shared/abis/collection.json');
            return module.default;
          }
      
          case 'loan': {
            const module = await import('../../shared/abis/nft-loan.json');
            return module.default;
          }

          case 'erc20': {
            const module = await import('../../shared/abis/erc20.json');
            return module.default;
          }

          case 'lazyMint': {
            const module = await import('../../shared/abis/lazy-mint-nft.json');
            return module.default;
          }

          case 'exchange': {
            const module = await import('../../shared/abis/exchange.json');
            return module.default;
          }
      
          case 'syndicatePool': {
            const module = await import('../../shared/abis/syndicate-pool.json');
            return module.default;
          }

          case 'onboardAsset': {
            const module = await import('../../shared/abis/onboard-deposit-vault.json');
            return module.default;
          }

          default:
            throw new Error(`ABI type not supported: ${type}`);
        }
      }
      
      

    private serializeContractParameters(parameters: any): any {
        if (Array.isArray(parameters)) return parameters.map((p) => this.serializeContractParameters(p));
        if (typeof parameters === 'bigint') return parameters.toString();
        if (parameters && typeof parameters === 'object') {
            const out: any = {};
            for (const key of Object.keys(parameters)) {
                out[key] = this.serializeContractParameters(parameters[key]);
            }
            return out;
        }
        return parameters;
    }

    async sendTransactionRegulated(params: any, args: any, transanctionId?: string) {
        let user = JSON.parse(this.webStorageService.getLocalStorage('user') || '{}');
        params.accountId = user.fire_block_address;
        params.from = params.account;
        params.contractAddress = params.to;
        params.function = args.functionName;
        params.parameters = this.serializeContractParameters(args.args);
        params.abi_type = args.abiType;
        params.transaction_id = transanctionId
        delete params.account;
        delete params.data;
        delete params.chainId;
        delete params.gasPrice;
        delete params.abi;
        try {
            const result = await this.http.post(`${environment.API_BASE_URL_V3}/contract-transaction`, params).toPromise();
            return result;
        } catch (error) {
            console.error('Error fetching data:', error);
            return Promise.reject(error)
        }
    }

    /**
     * Sends transaction unregulated
     * @param params 
     * @param account 
     * @param [transanctionId] 
     * @returns  
     */
    async sendTransactionUnregulatedOld(params: any, account: any, transanctionId?: string) {
        try {
            let txData: { [key: string]: any } = {
                status: 0
            }

            // await reconnect(wagmi_config)  // Re-connect the wagmi config
            const config = await prepareTransactionRequest(this.appkitService.adapter.wagmiConfig, params);
            txData['data'] = config?.data ? config?.data : '' //Pass The meta data to the transaction log.
            await this.transactionService.createTransaction(txData, transanctionId);
            let hash = await sendTransaction(this.appkitService.adapter.wagmiConfig, config);
            delete txData['data'] // Delete the data key after geting the hash and update the hash.

            // NOTE: Updates the transaction with the transaction hash and sets the status to 'In Progress'
            txData['transaction_hash'] = hash
            await this.transactionService.createTransaction(txData, transanctionId);

            return await waitForTransactionReceipt(this.appkitService.adapter.wagmiConfig, { hash, confirmations: 1 });
        } catch (error: any) {
            // NOTE: The following if-else structure is implemented to handle specific error cases:
            // 1. If the error type is an INTERNAL JSON-RPC error, which often follows an IntegerOutOfRange error,
            //    we explicitly reject the Promise with this error, as it's part of a known issue sequence.
            // 2. For other types of errors, we throw the error to allow further error handling mechanisms to catch it.
            if (error.details === INTERNAL_JSON_RPC_ERROR) {
                return Promise.reject(new Error(error.details))
            }
            else {
                throw error
            }
        }
    }

    /**
     * Sends an unregulated transaction using wagmi
     * @param params The parameters of the transaction
     * @param args The arguments of the transaction
     * @param [transactionId] The optional transaction ID to store the transaction data against
     * @returns A promise that resolves with the transaction hash, or rejects with an error
     * @throws {Error} If the transaction fails for any reason
     */
    async sendTransactionUnregulated(params: any, args: any, transactionId?: string) {
        try {
            let txData: { [key: string]: any } = {
                status: 0
            }

            // Pre-flight: native gas balance check. If the wallet has zero
            // native token, the wagmi/RPC failure surfaces as a generic
            // "insufficient funds" error that's easily confused with an ERC-20
            // balance issue. Failing fast here gives the user an unambiguous
            // gas-specific message. We only block at literal zero — any non-
            // zero balance lets viem attempt estimateGas, since the precise
            // gas requirement varies per call.
            try {
                const nativeBal = await getBalance(this.appkitService.adapter.wagmiConfig, {
                    address: params.account as `0x${string}`
                });
                if (nativeBal && nativeBal.value === 0n) {
                    throw new Error(
                        `Insufficient native currency (gas) on your wallet. Add the chain's native token (e.g. POL on Polygon) to cover gas fees and retry.`
                    );
                }
            } catch (preflightErr: any) {
                // Only surface the explicit zero-balance message; if the
                // balance read itself failed for any other reason, swallow
                // and let writeContract proceed (we don't want a network
                // hiccup to block the user).
                if (preflightErr?.message?.startsWith('Insufficient native currency')) {
                    throw preflightErr;
                }
            }

          // Send transaction using wagmi
          const hash = await writeContract(this.appkitService.adapter.wagmiConfig, {
            abi: params.abi,       // FIXED
            address: params.to,
            functionName: args.functionName,
            args: args.args,
            value: params.value,   // FIXED (BigInt)
            ...(await walletFeeOverrides(this.appkitService.adapter.wagmiConfig)),
          });

          return await waitForTransactionReceipt(this.appkitService.adapter.wagmiConfig, { hash, confirmations: 1 });

        } catch (error: any) {

          // NOTE: Handling JSON-RPC internal error sequence
          if (error.details === INTERNAL_JSON_RPC_ERROR) {
            return Promise.reject(new Error(error.details));
          } else {
            throw error;
          }
        }
      }
      

    /**
     * Encodes function data and estimates gas for a contract call
     * @param contractParams - Contract parameters including ABI, function name, args, etc.
     * @param gasBuffer - Gas buffer percentage (default: 25%)
     * @param fallbackGas - Fallback gas amount if estimation fails (default: 200000n)
     * @returns Object containing encoded data, gas estimate, and success status
     */
    async encodeDataAndEstimateGas(
        contractParams: ContractParams,
        gasBuffer: number = 25,
        fallbackGas: bigint = 200000n
    ): Promise<GasEstimationResult> {
        try {
        // Encode the function data
        const encodedData = encodeFunctionData({
            abi: contractParams.abi,
            functionName: contractParams.functionName,
            args: contractParams.args
        });
    
        let gasEstimate: bigint;
        let success = true;
        let error: string | undefined;
        let errorRaw: any | undefined;
    
            try {
                // Estimate gas for the transaction
                gasEstimate = await estimateGas(this.appkitService.adapter.wagmiConfig, {
                account: contractParams.account,
                to: contractParams.address,
                data: encodedData,
                value: contractParams.value || 0n
                });
        
                // Apply gas buffer
                gasEstimate = (gasEstimate * (100n + BigInt(gasBuffer))) / 100n;
        
                // console.log('Gas estimate with buffer:', gasEstimate);
            } catch (gasError) {
                // console.warn('Gas estimation failed, using fallback:', gasError);
                gasEstimate = fallbackGas;
                success = false;
                error = gasError instanceof Error ? gasError.message : 'Gas estimation failed';
                errorRaw = gasError;
            }
    
            const resultObj: GasEstimationResult = {
                encodedData,
                gasEstimate,
                success,
                error
            };
            // Attach raw error for callers that need detailed messages
            if (!success) (resultObj as any).errorRaw = errorRaw;
            return resultObj;
        } catch (encodingError) {
            const resultObj: GasEstimationResult = {
                encodedData: '0x' as `0x${string}`,
                gasEstimate: fallbackGas,
                success: false,
                error: encodingError instanceof Error ? encodingError.message : 'Function encoding failed'
            };
            (resultObj as any).errorRaw = encodingError;
            return resultObj;
        }
    }

    async sendTransactionNew(
        contractParams: ContractParams,
        abiType: string,
        transactionId?: string,
    ) {
        try {
            const regulatedFlag = this.walletStateService.getRegulated();
            const user = JSON.parse(this.webStorageService.getLocalStorage('user') || '{}');
            const regulated = regulatedFlag && !!user.fire_block_address;
            // Call the encode and gas estimation function
            const result = await this.encodeDataAndEstimateGas(contractParams);

            if (!result.success) {
                // Re-throw the original error so UI can read contract error details
                if ((result as any).errorRaw) throw (result as any).errorRaw;
                // Fallback to message-only error
                throw new Error(`${result.error}`);
            }

            if (regulated) {
                const sanitizedArgs = contractParams.args.map((arg: any) =>
                    typeof arg === "bigint" ? arg.toString() : arg
                );
                const params: any = {
                    accountId: user.fire_block_address,
                    from: contractParams.account,
                    contractAddress: contractParams.address,
                    function: contractParams.functionName,
                    parameters: sanitizedArgs,
                    abi_type: abiType,
                    transaction_id: transactionId,
                    value:contractParams.value ? regulated ? Number(parseEther(contractParams.value.toString())) : parseEther(contractParams.value.toString()) : 0
                }
                return this.sendFireBlockTransaction(params);
            } else {
                return this.sendContractTransactionWithDataGasEstimation(contractParams, result, transactionId);
            }
        } catch (error) {
            throw error
        }

    }

    private async sendFireBlockTransaction(
       fireBlockParams : any
    ) {
        try {
            const result = await this.http.post(`${environment.API_BASE_URL_V3}/contract-transaction`, fireBlockParams).toPromise();
            return result;
        } catch (error) {
            console.error('Error fetching data:', error);
            return Promise.reject(error)
        }

    }

    private async sendContractTransactionWithDataGasEstimation(
        contractParams: ContractParams,
        encodedDataWithGas: any,
        transactionId?: string
    ) {
        try {
            let txData: { [key: string]: any } = {
                status: 0
            };
    
            // Store transaction data
            txData['data'] = encodedDataWithGas.encodedData;
            txData['estimated_gas'] = encodedDataWithGas.gasEstimate.toString();
            await this.transactionService.createTransaction(txData, transactionId);
    
            // Execute the contract write
            const hash = await writeContract(this.appkitService.adapter.wagmiConfig, {
                abi: contractParams.abi,
                address: contractParams.address as `0x${string}`,
                functionName: contractParams.functionName,
                args: contractParams.args,
                account: contractParams.account as `0x${string}`,
                gas: encodedDataWithGas.gasEstimate.toString(),
                value: contractParams.value,
                ...(await walletFeeOverrides(this.appkitService.adapter.wagmiConfig)),
            });
    
            // Update transaction with hash
            delete txData['data'];
            txData['transaction_hash'] = hash;
            await this.transactionService.createTransaction(txData, transactionId);
    
            // Wait for transaction confirmation
            return await waitForTransactionReceipt(this.appkitService.adapter.wagmiConfig, { hash, confirmations: 1 });
    
        } catch (error: any) {
            console.error('Transaction error:', error);
            // Preserve specific RPC messages, with a friendly override for common gas error
            if (error?.code === -32000 && error?.message?.includes('gas required exceeds allowance')) {
                return Promise.reject(new Error('Estimated gas is insufficient. Please try with higher gas limit.'));
            }
            
            if (error.details === INTERNAL_JSON_RPC_ERROR) {
                return Promise.reject(new Error(error.details));
            }
            
            throw error;
        }
    }
    



    getNetwork = (chainId: string) => {
        let network;
        let key;
        let networkId;
        switch (chainId) {
            case '0x1':
            case '1':
                chainId = '0x1';
                networkId = 1;
                network = 'Ethereum Mainnet';
                key = 'ETH';
                break;
            case '0x5':
            case '5':
                chainId = '0x5';
                networkId = 5;
                network = 'Goerli Testnet';
                key = 'GoerliETH';
                break;
            case '0x13882':
            case '80002':
                chainId = '0x13882';
                networkId = 80002;
                network = 'Polygon Testnet';
                key = 'MATIC';
                break;
            case '0x89':
            case '137':
                chainId = '0x89';
                networkId = 137;
                network = 'Polygon Mainnet';
                key = 'MATIC';
                break;
            default:
                network = 'Unknown';
                break;
        }
        return { network, key, chainId, networkId };
    }

    async getNativeBalance(account: any) {
        return await getBalance(this.appkitService.adapter.wagmiConfig , {
            address: await getAddress(account?.walletAddress),
            chainId: Number(account.networkId)
        });
    }

    async getTokenBalance(account: any, tokenAddress: any) {
        return await getBalance(this.appkitService.adapter.wagmiConfig , {
            address: await getAddress(account?.walletAddress),
            chainId: Number(account.networkId),
            token: tokenAddress
        });
    }

    /** ERC-20 decimals never change, so they are read once per chain + token and reused. */
    private readonly tokenDecimalsCache = new Map<string, number>();

    /**
     * Reads the ERC-20 balance of every token in one multicall instead of one
     * getTokenBalance() request per token. Decimals are only part of the call the
     * first time a token is seen. Resolves to a map keyed by the lower-cased token
     * address; a token whose read fails is left out, so the caller keeps its
     * previous value. If the multicall itself fails, falls back to per-token reads.
     */
    async getTokenBalances(account: any, tokenAddresses: string[]): Promise<Record<string, number>> {
        const chainId = Number(account?.networkId);
        const owner = getAddress(account?.walletAddress);
        const tokens = [...new Set(tokenAddresses.filter(Boolean).map((address) => getAddress(address)))];
        const balances: Record<string, number> = {};
        if (!tokens.length) { return balances; }

        const decimalsKey = (token: string) => `${chainId}:${token.toLowerCase()}`;
        const needDecimals = tokens.filter((token) => !this.tokenDecimalsCache.has(decimalsKey(token)));
        const contracts = [
            ...tokens.map((token) => ({ address: token, abi: erc20Abi, functionName: 'balanceOf', args: [owner], chainId })),
            ...needDecimals.map((token) => ({ address: token, abi: erc20Abi, functionName: 'decimals', chainId }))
        ];

        let results: Array<{ status: 'success' | 'failure'; result?: unknown }>;
        try {
            results = await this.readTokenBalanceContracts(contracts);
        } catch {
            await Promise.all(tokens.map(async (token) => {
                try {
                    const balance = await this.getTokenBalance(account, token);
                    balances[token.toLowerCase()] = Number(balance?.formatted || 0);
                } catch { /* keep the caller's previous value for this token */ }
            }));
            return balances;
        }

        needDecimals.forEach((token, index) => {
            const decimals = results[tokens.length + index];
            if (decimals?.status === 'success') {
                this.tokenDecimalsCache.set(decimalsKey(token), Number(decimals.result));
            }
        });
        tokens.forEach((token, index) => {
            const balance = results[index];
            const decimals = this.tokenDecimalsCache.get(decimalsKey(token));
            if (balance?.status === 'success' && decimals !== undefined) {
                balances[token.toLowerCase()] = Number(formatUnits(balance.result as bigint, decimals));
            }
        });
        return balances;
    }

    /** The multicall behind getTokenBalances(); a separate method so specs can stub the RPC. */
    protected readTokenBalanceContracts(contracts: any[]): Promise<Array<{ status: 'success' | 'failure'; result?: unknown }>> {
        return readContracts(this.appkitService.adapter.wagmiConfig, { contracts, allowFailure: true }) as any;
    }

    /**
     * get token price from databse
     * @param {string} tokenId
     */
    getTokenPriceFromDb(tokenId: string) {
        return this.http.get(`${environment.API_BASE_URL}/user/digital-value?address=${tokenId}`);

    }

    async getImage(media: any) {
        // Raw fetch bypasses every interceptor, so it needs its own bound. Without this a
        // stalled IPFS gateway hangs the caller indefinitely on a throttled connection.
        //
        // Only the Content-Type is wanted, and fetch resolves as soon as the headers land,
        // so the body is never read. It used to await response.blob(), which pulled the whole
        // file down before answering -- multi-megabyte PDFs routinely outran the 20s abort, and
        // the caller then saw a failure and treated the document as having no type at all.
        return fetch(media, { signal: AbortSignal.timeout(20000) })
            .then(response => {
                // Nothing consumes the body; cancelling frees the connection rather than
                // leaving a download streaming in the background.
                response.body?.cancel().catch(() => undefined);
                return { contentType: response.headers.get("Content-Type") };
            })
            .catch(error => console.log('fetch image'))
    }

    /**
     * Builds an NFT's media gallery and returns it straight away, filling in each file's type
     * as it becomes known.
     *
     * Gateway URLs carry no extension, so a file's type comes from the Content-Type of a request
     * for it. The galleries used to wait for that before listing the file, which held the main
     * image back by a whole gateway round trip (often 1-2s) before its own download even began.
     * An entry with no type yet renders as an image - almost all media are - and a PDF or 3D
     * model switches to its own viewer once its type arrives.
     *
     * Each lookup updates the entry it was started for, so a slow answer can never land in the
     * gallery of an asset the user has since switched to.
     *
     * @param urls Primary media first, then secondary media. Blank and non-string values are skipped.
     * @param preview The NFT's preview image, shown for HTML and 3D media.
     * @param resolveFileType Maps a Content-Type to the gallery's file type; defaults to its subtype.
     * @returns The gallery entries, in order.
     */
    buildMediaGallery(
        urls: unknown[],
        preview: string | undefined,
        resolveFileType: (contentType: string | null | undefined, url: string) => string | undefined =
            (contentType) => contentType?.split('/')[1]
    ): NftMediaItem[] {
        return urls
            .filter((url): url is string => typeof url === 'string' && !!url.trim())
            .map((url) => {
                const extension = url.split('.').pop();
                if (extension === 'html') return { url, fileType: extension, preview };

                const item: NftMediaItem = { url, preview };
                this.getImage(url).then((data: any) => {
                    item.fileType = resolveFileType(data?.contentType, url);
                });
                return item;
            });
    }

    public closeAllModals(close: boolean) {
        this.closeModals.next(close)
    }

    getGoldPrice() {
        // Live price — short TTL keeps it fresh while collapsing the burst of
        // duplicate calls fired by sibling components/routes on page load.
        return this.getCachedPromise('goldPrice', () =>
            firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/get-gold-value`)));
    }

    calculateGoldValue = async (size: string, markupFee: number, goldValue: number = 0) => {
        // Define the conversion factors
        const OUNCE_TO_GRAM = 31.1035;
        const TOLA_TO_GRAM = 11.66;
        const KILO_TO_GRAM = 1000;
        let OUNCE_TO_USD: number;
        if (goldValue > 0) OUNCE_TO_USD = goldValue;
        else {
            let marketprice: any = await this.getGoldPrice();
            OUNCE_TO_USD = marketprice.data.gold_value
        }

        if (size?.includes(environment.ONE_KILO_GRAM)) {
            const weightInGrams = 1 * KILO_TO_GRAM;
            const weightInOunces = weightInGrams / OUNCE_TO_GRAM;
            const valueInUSD = weightInOunces * OUNCE_TO_USD;
            const totalUSD = valueInUSD + (valueInUSD * markupFee) / 100;
            return { price: valueInUSD, priceWithFee: totalUSD };
        } else if (size?.includes(environment.TEN_TOLA_BAR)) {
            const weightInGrams = 10 * TOLA_TO_GRAM;
            const weightInOunces = weightInGrams / OUNCE_TO_GRAM;
            const valueInUSD = weightInOunces * OUNCE_TO_USD;
            const totalUSD = valueInUSD + (valueInUSD * markupFee) / 100;
            return { price: valueInUSD, priceWithFee: totalUSD };
        } else if (size?.includes(environment.ONE_OUNCE)) {
            const totalUSD = OUNCE_TO_USD + (OUNCE_TO_USD * markupFee) / 100;
            return { price: OUNCE_TO_USD, priceWithFee: totalUSD };
        } else return 0
    }

    /**
     * Calculates platinum
     * @param ounce 
     * @param weight 
     * @returns  
     */
    calculatePlatinum(ounce: any, weight: any) {
        const OUNCE_TO_GRAM = 31.1035;
        const perGramPrice = ounce / OUNCE_TO_GRAM
        return weight * perGramPrice
    }


    /**
     * get time remaining from current time
     */
    getTimeRemaining(endDate: string | Date): string {
        const end = new Date(endDate).getTime();
        const now = new Date().getTime();
        const timeDiff = end - now;

        if (timeDiff < 0) {
            return ''
        }

        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

        return `(in ${days} days, ${hours} hours, ${minutes} minutes).`;

    }

    tenureDuriation: number = 0

    calculateInterest(loanAmount: number, loanDuration: number, loanPercentage: number, chainId: any) {
        this.tenureDuriation = this.tenureDaycalculation(loanDuration);
        let divisor = (environment as any)[chainId].LOAN_IN_DAYS ? (365 * 100) : (365 * 24 * 100)
        let interest_amount = (loanAmount * loanDuration * loanPercentage) / divisor;
        let total_amount = Number(loanAmount) + Number(interest_amount);
        let perEmi = interest_amount ? interest_amount / this.tenureDuriation : 0
        let emiAmount = perEmi.toFixed(2) as any
        return { interest_amount, total_amount, emiAmount }
    }

    /**
   * Tenures daycalculation
   * @param [loan_duration_days]
   * @returns
   */
    tenureDaycalculation(loanDuration: number = 0) {
        return Math.ceil(loanDuration / 30)
    }

    /**
     * Gets the appraisal value of an NFT.
     *
     * @param {any} nft - The NFT object containing details like category, lazy mint status, and sale details.
     * @returns {string | number} The appraisal value, which can be a number or a dash ('-') if the value is not available.
     */
    getAppraisalValue(nft: any): string | number {
        if (!nft) {
            return '-';
        }

        if (isDigitalAssetCategory(nft?.attributes)) {
            return Math.floor(getAssetAppraisal(nft)) || '-';
        }

        const isGoldCategory = nft.category === 'gold';
        const isLazyMint = isGoldCategory ? nft?.collections?.isLazyMint : nft?.lazy_mint

        const marketPrice = Math.ceil(nft?.marketPrice);
        if (isLazyMint) {
            const exchangePrice = Math.ceil(nft.sale_details?.exchange_price);
            const salePrice = Math.ceil(nft.sale_details?.price);

            if (isGoldCategory) {
                return marketPrice || '-';
            } else {
                return exchangePrice > 0 ? salePrice : Math.ceil(nft.appraisal_value) || '-';
            }
        } else {
            if (isGoldCategory) {
                return marketPrice || '-';
            } else {
                return Math.ceil(nft.appraisal_value) || '-';
            }
        }

    }
    /**
     * Triggers an email and in-app notification when a transaction fails due to insufficient funds.
     *
     * @param {string} id - The ID of the transaction that failed.
     * @param {string} type - The type of transaction (e.g., "bid").
     */
    async notifyInsufficientFunds(id: string, type: string): Promise<void> {
        try {
            await firstValueFrom(
                this.http.get(`${environment.API_BASE_URL_V3}/transaction-fail-notify?id=${id}&type=${type}`)
            );
        } catch (error) {
            this.toastr.error('An error occurred while notifying insufficient funds.');
            console.error(error);
        }
    }

    /**
     * Navigates to the collection detail page when a collection is clicked from the "NFTs in collection" section.
     * @param event The click event.
     * @param collectionAddress The address of the collection.
     */
    navigateToCollectionPage(event: MouseEvent, collectionAddress: string): void {
        event.stopPropagation();
        event.preventDefault();
        this.router.navigate([`/collection-detail/${collectionAddress}`]);
    }

    /**
     * Signs a message using the connected account.
     *
     * @returns {Promise<string>} A promise that resolves to the signature of the message.
     * @throws {Error} If the signing process fails.
     */
    async getSignature() {
        const signature = await signMessage(this.appkitService.adapter.wagmiConfig, {
            message: 'By approving this signature, you agree to the Privacy Policy and User Agreement.',
        })
        return signature
    }

    // Hide for reference purpose
    // getDocument() {
    //     const serviceTypes = ['privacy-policy', 'user-agreement'];
    //     const sources = serviceTypes.map((type) =>
    //         this.http.get(`${environment.API_BASE_URL}/file-upload/policy-document`, {
    //             headers: { 'service_type': type }
    //         })
    //     );

    //     forkJoin(sources).subscribe({
    //         next: (responses: any[]) => {
    //             this.docUrls = serviceTypes.reduce((acc, type, index) => {
    //                 const fileUrl = responses[index]?.data?.file?.[0]?.Url || null;
    //                 return { ...acc, [`${type}_url`]: fileUrl };
    //             }, {});
    //             console.log('Document URLs:', this.docUrls);
    //         },
    //         error: (err: any) => {
    //             console.error('Error fetching documents:', err);
    //         }
    //     });
    // }

    private documentUrls$: Observable<Record<string, string | null>> | null = null;

    /**
     * Gets document URLs for privacy policy and user agreement.
     * @returns An observable that emits an object containing the document URLs.
     */
    getDocument() {
        if (!this.documentUrls$) {
          const serviceTypes = ['privacy-policy', 'user-agreement'];

          const sources = serviceTypes.map(type =>
            this.http.get(`${environment.FILE_UPLOAD}/file-upload/policy-document`, {
              headers: { service_type: type }
            })
          );

          this.documentUrls$ = forkJoin(sources).pipe(
            map((responses: any[]) => {
              return serviceTypes.reduce((acc, type, index) => {
                const fileUrl = responses[index]?.data?.file?.[0]?.Url || null;
                acc[`${type}_url`] = fileUrl;
                return acc;
              }, {} as Record<string, string | null>);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
          );
        }

        return this.documentUrls$;
    }

    /**
     * Checks nft status if the selcted nft is already in open loan offer or not
     * @template C 
     * @param nftId  its like an array ['NftId']
     * @param callBack 
     */
    async checkNftStatus<A, C>(request: A, callBack: (status: any) => C) {
        try {
            const response: any = await firstValueFrom(
                this.http.post(`${environment.API_BASE_URL_V3}/nft/open-loan-offer-check`, request)
            );
            if (response) callBack(response.data.is_open_loan_offer);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Updates open loan offer status
     * @param nftId 
     */
    updateOpenLoanOfferStatus(request: any): Observable<any> {
        return this.http.patch(`${environment.API_BASE_URL_V3}/nft/cancel-open-loan-offers`, request)
    }

    /**
     * Handles expiry date
     * @param loan_request_days 
     * @param current_date 
     * @param LOAN_IN_DAYS 
     * @returns  
     */
    handleExpiryDate(loan_request_days: any, current_date: any, LOAN_IN_DAYS: any) {
        return dayjs(current_date).add(loan_request_days, LOAN_IN_DAYS ? 'days' : 'hours').toISOString()
    }

    /**
     * Process image
     * @param nft 
     * @returns  
     */
    processImage(nft: any) {
        let processImage;
        if (nft.fileType === 'html') {
            processImage = {
                ...nft,
                displayImage: nft.preview_image ? nft.preview_image : nft.secondary_media[0]
            };
        } else {
            processImage = {
                ...nft,
                displayImage: nft.preview_image ? nft.preview_image : nft.primary_media
            };
        }
        return processImage;
    }

    /**
     * Profiles image upload
     * @param {File} file
     * @returns
     */
    profileImageUpload(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('service_type', 'image');
        // HttpClientXsrfModule will automatically attach X-XSRF-TOKEN for this
        // mutating request when the backend has set the XSRF-TOKEN cookie.
        return this.http.post(`${environment.FILE_UPLOAD}/file-upload/document`, formData)
    }

    /**
     * Updates users
     * @param data
     * @returns
     */
    public updateUsers(data: object) {
        return this.http.patch(`${environment.API_BASE_URL_V3}/user`, data);
    }

     /**
     * Gets user metrics
     * @returns  
     */
    getUserMetrics () {
        return this.http.get(`${environment.API_BASE_URL_V3}/user-dashboard-metrics`);
    }

    /**
     * Gets precious metal values
     * @returns  
     */
    getPreciousMetalValues() {
        // Live price — short TTL (see getGoldPrice).
        return this.getCachedPromise('metalValues', () =>
            firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/get-metal-values`)));
    }
    /**
     * Latest Digital Assets prices keyed by lowercase Type, e.g.
     * `{ bitcoin: { usd_price, as_of, stale } }`. Live price — same short TTL as metals.
     */
    getDigitalAssetValues() {
        return this.getCachedPromise('digitalAssetValues', () =>
            firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/get-digital-asset-values`)));
    }
    /**
        * Filters attributes
        * @param data
        * @param key
        * @returns
        */
    filterAttributes(filterData: any = [], key: string, mapKey: any) {
        if (Array.isArray(filterData) && filterData.length != 0) {
            const filterValue = filterData.filter((res) => res[mapKey].toLowerCase() == key);
            return filterValue.length != 0 ? filterValue[0]['value'] : '0'
        } else {
            return ''
        }
    }
    /**
     * Handles current market price
     * @param attribute 
     * @param metal_price // Not ; Market Price Consider As Markup Fee
     * @returns  
     */
    handleCurrentMarketPrice(attribute: any, metal_price: any,) {
        const OUNCE_TO_GRAM = 31.1035;
        let markup_fee_percentage = this.filterAttributes(attribute, 'markup fee', 'key')
        let attr_weight = this.filterAttributes(attribute, 'weight', 'key');
        const weight = parseFloat(attr_weight.replace(/g$/i, ''));
        const converted_markup_fee_percentage = markup_fee_percentage ? Number(markup_fee_percentage) : 0
        // console.log('metal_price', metal_price);
        const price_per_gram = metal_price / OUNCE_TO_GRAM
        // console.log('price_per_gram', price_per_gram);
        const actual_metal_price = price_per_gram * weight
        // console.log('actual_metal_price', actual_metal_price);
        const markupPercentage = Number(converted_markup_fee_percentage) / 100;
        const mark_up_fee = markupPercentage * actual_metal_price;
        // console.log('mark_up_fee', mark_up_fee);
        const appraisalPrice = attribute.filter((res: any) => res.key.toLowerCase() === 'appraisal value');
        const appraisalValue = appraisalPrice.length > 0 ? appraisalPrice[0].value : '0';
        // console.log('appraisalValue---', appraisalValue, mark_up_fee, actual_metal_price);
        return { pricewithgram: actual_metal_price, market_price: mark_up_fee + actual_metal_price }
    };

    /**
     * Handles oauth login
     * @param user here we pass the User Details for the 
     */
    async _handleOauthLogin(user: any) {
        const obj = {
            id_token: user.idToken,
            apple_signin: false,
            google_signIn: true,
            type: 'login'
        }
        try {
            const response: any = await firstValueFrom(this.accountService.googleAuth(obj));
            if (response) {
                this.walletStateService.setRegulated(true);
                const accountData = { walletAddress: response.data.wallet_address, chainId: environment.DEFAULT_CHAIN, walletProvider: "", networkId: Number(environment.DEFAULT_NETWORK), init: false };
                this.webStorageService.setLocalStorage('user', JSON.stringify(response.data));
                this.walletStateService.setWallet(accountData);
                // Update user state service to ensure header shows correct user
                this.userState.setUser(response.data);
                this.userState.setAccount(accountData);
                location.reload() //Need to set the user details and other stuffs.
            }
        } catch (error: any) {
            this.toastr.error(error?.error?.message || "Something went wrong, try again later.");
        }
    }
    /**
     * Gets image validation
     */
    public get fileExtension(): Array<{ docmentExtension: string[], mediaExtension: string[] }> {
        return [{ "docmentExtension": [...documentFileExtension], 'mediaExtension': [...imageFileExtension, ...mediaFileExtension] }]
    }


    handlePlatformfeeText(platformFee: number = 0, isPercentage: boolean = false, currency?: any, isLastContent: string = '') {
        return isPercentage ? this.handlePercentage(platformFee, isLastContent) : this.handleAmount(platformFee, currency, isLastContent)
    }

    /**
     * Handles fee combiner
     * @param [fee1] 
     * @param [fee2] 
     * @returns  
     */
    handleFeeCombiner(fee1: number = 0, fee2: number = 0) { /// May be this is was vary based on our fee kindly check 
        return (fee1 == 0 || fee2 == 0) ? '' : ' + '
    }
    /**
     * Rounds a value for display so binary floating-point artifacts never reach the UI.
     * Fee percentages are summed (accept + repay + spread), and sums like 5.3 + 2.5
     * produce 7.800000000000001 — this trims that back to 7.8.
     * @param [value]
     * @param [maxDecimals]
     * @returns the rounded number, with trailing zeros dropped
     */
    formatDecimal(value: number = 0, maxDecimals: number = 4) {
        const numericValue = Number(value)
        return Number.isFinite(numericValue) ? Number(numericValue.toFixed(maxDecimals)) : 0
    }

    handlePercentage(value: number = 0, content: string = '') {
        return value == 0 ? '' : `${this.formatDecimal(value)}%${content}`
    }
    handleAmount(value: number = 0, currency: any, content: string = '') {
        const formattedValue = this.currencyAmountPipe.transform(
            value,
            currency
          );
        return value == 0 ? '' : `${formattedValue}${content}`
    }

    /**
     * Handles nofee applied
     * @param [value] 
     * @param [currency] 
     * @returns  
     */
    handleNofeeApplied(value: number = 0, currency?: any, lastContent: string = '') {
        const formattedValue = this.currencyAmountPipe.transform(
            value,
            currency
          );
        return value == 0 ? 'No fee applied' : `${formattedValue} ${lastContent}`
    }

    /**
     * Handles platform fee text
     * @param [value] 
     * @param [custom_text] 
     * @param [not_applied] 
     * @returns  
     */
    handlePlatformFeeText(value: number = 0, not_applied: string = '', custom_text: string = '') {
        return value == 0 ? not_applied : custom_text
    }

    /**
     * Determines whether policy updated user is
     * @param user 
     * @param privacyPolicyModal 
     * @returns true if policy updated user 
     */
    isPolicyUpdatedUser(user: any, privacyPolicyModal: any): boolean {
        if ((user?.is_policy_accepted === false || user?.is_policy_accepted === undefined) && user.email_verified
        ) {
            privacyPolicyModal?.show();
            return true;
        }
        return false;
    }

    /**
     * Updates policy status
     * @param id 
     * @param params 
     * @returns policy status 
     */
    updatePolicyStatus(id: string, params: any): Observable<any> {
        return this.http.patch(`${environment.API_BASE_URL_V3}/user/update-policy-status?id=${id}`, params)
    }

    /**
     * Gets transack link
     */
    getTransackLink() {
    return this.http.get(`${environment.API_BASE_URL_V3}/transak/create-widget-url`)
    }


    getNftUrl(logsDetails: any): string {
        if (logsDetails?.token_id === undefined || logsDetails?.token_id === null) return '';
        const nft = logsDetails?.nft_ids?.[0] || logsDetails
        // const baseUrl = environment.MARKETPLACE_URL;

        const path = nft.lazy_mint
            ? `lazy-mint/${nft._id}`
            : `nft-detail/${nft.collections?.collection_address}/${nft.token_id}`;
        return `${path}`;
    }

    /**
     * Withdraws usdc
     * @param body 
     * @returns usdc 
     */
    withdrawUsdc(body: any): Observable<any> {
        return this.http.post(`${environment.API_BASE_URL_V3}/fire-block/withdraw-to-external-wallet`, body)
    }


    /**
     * Clicks redirect
     * @param data 
     * @param user 
     * @param callBack 
     */
    clickRedirect(data: any, user: any, callBack: (route: string) => any) {
        const parsedData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
        const dataValue = data;
        let redirect_url = '';

        const loanDetailMethods = [
            MethodTypes.LOAN_DEFAULT,
            MethodTypes.REQ_LOAN,
            MethodTypes.UPDATE_LOAN,
            MethodTypes.FORECLOSE_LOAN,
            MethodTypes.LOAN_REPAID,
        ];

        const offerMethods = [
            MethodTypes.COUNTER_OFFER,
            MethodTypes.RECOUNT_OFFER,
            MethodTypes.BORROW_RECOUNT_OFFER,
        ];

        const nftDetailMethods = [
            MethodTypes.ITEM_MINTED,
            MethodTypes.PURCHASE_SELL_ORDER,
            MethodTypes.CREATE_ITEM_LOAN_BID,
            MethodTypes.BULK_MINT,
            MethodTypes.CREATE_SELL_ORDER,
            MethodTypes.CANCEL_ITEMBID,
            MethodTypes.CREATE_ITEMBID,
            MethodTypes.ACCEPT_LOAN,
            MethodTypes.BURN_ITEM,
            MethodTypes.CANCEL_LOAN,
            MethodTypes.ACCEPT_ITEMBID,
            MethodTypes.PURCHASE_PARTNER_ITEM,
            MethodTypes.ITEM_TRANSFER,
            MethodTypes.CANCEL_SELL_ORDER,
            MethodTypes.BUNDLE_PURCHASE,
            MethodTypes.ACCEPT_REFINANCE_LOAN
        ];

        const bulkLoanBidMethods = [
            MethodTypes.CANCEL_ITEM_LOAN_BID,
            MethodTypes.COUNTER_OFFER_OPEN_LOAN_BID,
            MethodTypes.CREATE_BULK_LOAN_BID,
            MethodTypes.ACCEPT_BULK_LOAN_BID,
            MethodTypes.CANCEL_BULK_LOAN_BID,
            MethodTypes.EDIT_BULK_LOAN_BID,
            MethodTypes.ACCEPT_ITEM_LOAN_BID,
        ];

        if (loanDetailMethods.includes(data.method)) {
            const { _id } = parsedData || {};
            if (_id && data?.data?.borrower_id?._id == user?._id) {
                redirect_url = `borrow-detail/${_id}`;
            } else if (_id) {
                redirect_url = `lending-detail/${_id}`
            }
        } else if (offerMethods.includes(data.method)) {
            const loanId = parsedData?.loan_request_id?._id;
            if (loanId) {
                redirect_url = `lending-detail/${loanId}`;
            }
        } else if (data.method === MethodTypes.PARTNER_COL_CREATED) {
            const collectionAddress = parsedData?.collection_address;
            if (collectionAddress) {
                redirect_url = `collection-detail/${collectionAddress}`;
            }
        } else if (nftDetailMethods.includes(data.method)) {
            const collectionAddress = dataValue?.nft_ids?.[0]?.collections?.collection_address;
            const tokenId = dataValue?.nft_ids?.[0]?.token_id;

            if (collectionAddress && tokenId !== undefined) {
                redirect_url = `nft-detail/${collectionAddress}/${tokenId}`;
            }
        } else if (bulkLoanBidMethods.includes(data.method)) {
            redirect_url = `/offer-loans/details/${parsedData._id}`;
        } else {
            console.warn(`Unhandled method type: ${data.method}`);
        }

        callBack(redirect_url);
    }
    /**
     * Gets full name
     * @param data 
     * @param [user] 
     * @returns  
     */
    getFullName(data: any, user?: any) {
        const name = data?.display_name || data?.name || user || '';
        return name?.length > 20 ? name : '';
    }

    /**
     * Initializes loan form values
     * @param value 
     * @param form 
     */
    initializeLoanFormValues(value: any, form: any) {
        const numericValue = Number(String(value).replace(/,/g, ''));
        if (isNaN(numericValue)) {
            return;
        }
        const perValue =  Math.ceil(numericValue * 0.5);
        form.patchValue({
            requested_loan_amount: perValue,
            loan_duration_days: 90,
            loan_percentage: 15,
            emi_option: loanRepayType[0].isEmi
        });
    }

    /**
     * Applys loan defaults ltv
     * @param value 
     * @returns  
     */
    applyLoanDefaultsLTV(value: any) {
        const perValue = value * 0.5
        const loanValue = perValue / value * 100;
        const loanamount = Number(loanValue.toFixed(2));
        return loanamount
    }


    /**
     * Gets location url
     * @returns  
     */
      getLocationUrl() {
        return this.http.get(`${environment.API_BASE_URL_V3}/nft-traits/location`)
        }
  /**
     * Calculates diff
     * @param {any} dateSent
     * @returns
     */
    calculateDiff(dateStr: any) {
        if (!dateStr) return '';
        const currentDate = new Date();
        const endDate = new Date(dateStr);
        const diffInHours = (endDate.getTime() - currentDate.getTime()) / 3600000; 
        let className = '';

        if (diffInHours <= 48) {
            className = 'maturity-red';       // within 2 days
        } else if (diffInHours <= 168) {
            className = 'maturity-orange';    // within 7 days
        } else if (diffInHours <= 720) {
            className = 'maturity-yellow';    // within 30 days
        } else {
            className = 'maturity-green';     // more than 30 days
        }

        return className;
    }


    
      /**
       * Renders pdf thumbnail
       * @param objectUrl 
       * @returns pdf thumbnail 
       */
      public async renderPdfThumbnail(objectUrl: string): Promise<string> {
        // Returns an image data URL on success, or '' when no thumbnail could
        // be produced. Callers gate the <img> thumbnail on this being truthy and
        // fall back to an <object> PDF viewer otherwise, so failures MUST be falsy.
        const docRef: any = (globalThis as any).document;
        // No DOM (SSR / non-browser) → skip loading the pdfjs chunk entirely.
        if (!docRef || !docRef.createElement) {
          return '';
        }
        try {
          const pdfjsMod: any = await import('pdfjs-dist');
          const pdfjs = pdfjsMod?.getDocument ? pdfjsMod : (pdfjsMod?.default ?? pdfjsMod);
          const { getDocument, GlobalWorkerOptions } = pdfjs;

          const workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
          GlobalWorkerOptions.workerSrc = workerSrc;

          const data = await fetch(objectUrl).then((r) => r.arrayBuffer());
          const pdf = await getDocument({ data }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 0.5 });
          const canvas: any = docRef.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) return '';
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: context, viewport }).promise;
          return canvas.toDataURL('image/png');
        } catch (err) {
          // Chunk load / render failure → return falsy so the <object> fallback renders.
          console.warn('renderPdfThumbnail failed → PDF <object> fallback', err);
          return '';
        }
      }
    
    
      /**
       * Determines whether heic file is
       * @param file 
       * @returns true if heic file 
       */
      public isHeicFile(file: File): boolean {
        // Testing purpose to add 
        // console.log("isHeicFile",file);
        
        const name = file.name || '';
        const type = (file.type || '').toLowerCase();
        return /\.(heic|heif)$/i.test(name) || type === 'image/heic' || type === 'image/heif';
      }
    
      /**
       * Determines whether excel file is
       * @param file 
       * @returns true if excel file 
       */
      public isExcelFile(file: File): boolean {
        const name = (file.name || '').toLowerCase();
        const type = (file.type || '').toLowerCase();
        return name.endsWith('.xlsx') || name.endsWith('.xls') ||
          type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          type === 'application/vnd.ms-excel';
      }
    
      /**
       * Parses excel
       * @param file 
       * @returns excel 
       */
      public async parseExcel(file: File): Promise<{ headers: string[]; rows: any[] }> {
        let XLSXmod: any;
        try {
          XLSXmod = await import('xlsx');
        } catch (err) {
          // Lazy chunk failed to load (network / deploy skew) — surface a clear
          // error to the caller's handler instead of a raw ChunkLoadError.
          console.warn('xlsx dynamic import failed', err);
          throw new Error('Excel preview is temporarily unavailable. Please try again.');
        }
        const XLSX = XLSXmod?.read ? XLSXmod : (XLSXmod?.default ?? XLSXmod);

        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        const [headerRow = [], ...bodyRows] = json;
        const headers = headerRow.map((h) => String(h));
        const rows = bodyRows.slice(0, 20); // limit rows for preview
        return { headers, rows };
      }
    
      /**
       * Converts heic to jpeg
       * @param file 
       * @returns heic to jpeg 
       */
  async convertHeicToJpeg(file: File): Promise<File> {
  const targetName = (file.name || 'image').replace(/\.(heic|heif)$/i, '.jpg');
  try {
    const heicToMod: any = await import('heic-to');
    const convertFn =
      typeof heicToMod === 'function'
        ? heicToMod
        : heicToMod.heicTo || heicToMod.default;

    if (typeof convertFn === 'function') {
      const out: Blob | Blob[] = await convertFn({
        blob: file,
        type: 'image/jpeg',
        quality: 0.9,
      });

      const blob = Array.isArray(out) ? out[0] : out;

      return new File(
        [blob.type === 'image/jpeg' ? blob : new Blob([blob], { type: 'image/jpeg' })],
        targetName,
        { type: 'image/jpeg' }
      );
    }
  } catch (err) {
    console.warn('heic-to conversion failed → fallback to canvas', err);
  }

  try {
    if (typeof globalThis.createImageBitmap === 'function' && typeof document?.createElement === 'function') {
      const bitmap = await createImageBitmap(file as any);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');

      ctx.drawImage(bitmap, 0, 0);

      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          'image/jpeg',
          0.9
        )
      );

      return new File([blob], targetName, { type: 'image/jpeg' });
    }
  } catch (err) {
    console.warn('Canvas fallback failed', err);
  }

  throw new Error('HEIC conversion failed (no supported converter found)');
}

/**
     * Gets custody paid until
     * @param nft
     * @returns custody paid until
     */
    getCustodyPaidUntil(nft: any): string {
        const attr = nft?.attributes?.find((item: any) => {
            const normalizedKey = String(item?.key || '')
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '');
            return normalizedKey === 'custodypaiduntil';
        });
        return String(attr?.value || '').trim();
    }

    /**
     * Sets the custody-paid-until date on an asset that already has the attribute, so a
     * card shows a just-paid storage fee without re-fetching the list.
     * @param nft
     * @param paidUntil new date, in the YYYY-MM-DD format the backend stores
     */
    setCustodyPaidUntil(nft: any, paidUntil: string): void {
        const attr = nft?.attributes?.find((item: any) =>
            String(item?.key || '').toLowerCase().replace(/[^a-z0-9]/g, '') === 'custodypaiduntil');
        if (attr) attr.value = paidUntil;
    }

    /**
     * Converts a selected custody "paid until" date into a canonical epoch (ms) anchored at UTC midnight.
     *
     * Custody dates are date-only (whole-day granularity) — paying extends from one calendar day to
     * another, no time-of-day. So the on-chain `custodyUntil` must be timezone-independent: we anchor to
     * UTC midnight of the chosen calendar day and always read it back in UTC, so "2026-07-26" stays
     * "2026-07-26" in every timezone. Using local midnight (`dayjs(date).valueOf()`) instead lands on
     * 00:00 local, which is the *previous* UTC day for positive offsets (e.g. IST +05:30 → 18:30 UTC the
     * prior day), shifting the custody date back by one when the chain value is read in UTC.
     *
     * Accepts a dayjs instance or a 'MM-DD-YYYY' / 'YYYY-MM-DD' string (the shapes `nextDuedate` takes).
     */
    toCustodyEpochMs(date: any): number {
        // Strict format parsing (customParseFormat is extended at module load) so 'MM-DD-YYYY'
        // and ISO strings resolve deterministically; fall back to the loose parser only if needed.
        const parsed = dayjs.isDayjs(date) ? date : dayjs(date, ['MM-DD-YYYY', 'YYYY-MM-DD'], true);
        const d = parsed && parsed.isValid() ? parsed : dayjs(date);
        if (!d.isValid()) return NaN;
        return Date.UTC(d.year(), d.month(), d.date());
    }

    /**
        * Uploads image
        * @param body
        * @returns
        */
    addWithdrawAddressRequest(body: any): Observable<any> {
        return this.http.post(`${environment.API_BASE_URL_V3}/add-withdraw-address-request`, body)
    }

    /**
     * Marks an item as old, so it won't be shown in the NFT list anymore.
     * IMPORTANT: This endpoint bypasses authentication, so it should only be called from trusted sources.
     * @param walletAddress The address of the wallet that owns the item to be marked as old
     * @returns The response from the server
     */
    markNewItemAsOld(walletAddress: string) {
        return this.http.post(
          `${environment.API_BASE_URL_V3}/regulated-nft/mark-old`,
          { wallet_address: walletAddress }
        );
    }
    
    /**
     * Gets the user's operating system
     * @returns The user's operating system
     * @example
     * const os = commonService.getUserOS();
     * console.log(os); // Windows, MacOs, Linux, Android, iOS or Unknown
     */
    getUserOS(): string {
        const ua = window.navigator.userAgent.toLowerCase();
      
        if (/android/.test(ua)) return 'Android';
        if (/iphone|ipad|ipod/.test(ua)) return 'iOS';
        if (/windows/.test(ua)) return 'Windows';
        if (/mac os x/.test(ua)) return 'MacOS';
        if (/linux/.test(ua)) return 'Linux';
      
        return 'Unknown';
    }      

    /**
     * Gets the user's browser
     * @returns The user's browser
     * @example
     * const browser = commonService.getUserBrowser();
     * console.log(browser); // Brave, Microsoft Edge, Opera, Mozilla Firefox, Safari, Google Chrome or Unknown
     */
    getUserBrowser(): string {
        const ua = navigator.userAgent.toLowerCase();
      
        // Brave (best effort)
        if ((navigator as any).brave?.isBrave) return 'Brave';
      
        if (ua.includes('edg')) return 'Microsoft Edge';
        if (ua.includes('opr') || ua.includes('opera')) return 'Opera';
        if (ua.includes('firefox')) return 'Mozilla Firefox';
      
        // Chrome must be AFTER Edge & Opera
        if (ua.includes('chrome')) return 'Google Chrome';
      
        // Safari (must be last among WebKit browsers)
        if (ua.includes('safari')) return 'Safari';
      
        if (ua.includes('msie') || ua.includes('trident')) return 'Internet Explorer';
      
        return 'Unknown';
      }

    /**
     * Returns the most relevant loan amount to use when calculating LTV.
     * Prefers resale amounts for resale loans, accepted loan amounts for active loans,
     * and requested loan amounts for pending requests.
     */
    getLoanAmountForLtv(loanRequest: any): number {
        if (loanRequest?.is_sell_request) {
            return Number(
                loanRequest?.loan_sell_request?.accepted_loan_sell_amount
                || loanRequest?.loan_sell_request?.loan_sell_amount
                || 0
            );
        }

        return Number(
            loanRequest?.accepted_loan_terms?.loan_amount
            || loanRequest?.requested_loan_amount
            || loanRequest?.loan_amount
            || 0
        );
    }

    /**
     * Calculates loan-to-value percentage for a loan request.
     * Prefers API-provided LTV values over frontend calculation.
     */
    calculateLoanToValue(appraisalValue: number | string, loanRequest: any): number {
        const apiLtv = Number(loanRequest?.accepted_loan_terms?.ltv || loanRequest?.ltv);
        if (apiLtv > 0) {
            return Number(apiLtv.toFixed(2));
        }

        const appraisal = Number(appraisalValue);
        const loanAmount = this.getLoanAmountForLtv(loanRequest);

        if (!appraisal || !loanAmount) {
            return 0;
        }

        return Number(((loanAmount / appraisal) * 100).toFixed(2));
    }


    /**
     * Maps a URL to its corresponding endpoint for the lending history API.
     * @param url - The URL to map to its endpoint.
     * @returns The mapped endpoint.
     * @example
     * const endpoint = commonService.getEndpoint('https://example.com/lending-history');
     * console.log(endpoint); // 'my-lending-list'
     */
      getEndpoint(url: string): string {
        if (url.includes('lending-history')) {
          return 'my-lending-list';
        }
    
        if (url.includes('live-borrowing')) {
          return 'live-borrowing-list';
        }
    
        if (url.includes('borrow-history')) {
          return 'borrowing-history-list';
        }
        if (url.includes('all-loan')) {
            return 'all-loan-requests';
        }
    
        return ''; // default fallback
      }
      

}
